import jsPDF from 'jspdf';
import { generateReceiptHeader, generateReceiptFooter, getCollegeInfo } from './receiptHeaderUtils';
import { ensureRupeeFont, formatINR } from './jspdfFonts';
import { formatDate } from '../utils/dateTimeUtils';

// Utility to generate consultant payment receipt PDF
export const generateConsultantReceipt = async (paymentData, collegeInfo = null) => {
  const doc = new jsPDF({
    putOnlyUsedFonts: true,
    compress: true
  });
  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  
  // Disable automatic page numbering
  doc.internal.pageNumbers = false;

  // Get college info - prefer parameter over localStorage
  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
    console.warn('generateConsultantReceipt: collegeInfo not passed, using localStorage fallback. This may cause tenant isolation issues.');
  }
  let yPos = await generateReceiptHeader(doc, collegeInfo, 'CONSULTANT PAYMENT RECEIPT');

  // Receipt Details
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');

  // Receipt Number and Date
  doc.text(`Receipt No:`, margin, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(`${paymentData.receipt_no || 'N/A'}`, margin + 35, yPos);
  
  doc.setFont(resolvedFont, 'normal');
  doc.text(`Date:`, pageWidth - margin - 60, yPos);
  doc.setFont(resolvedFont, 'bold');
  const dateStr = formatDate(paymentData.payment_date || new Date());
  doc.text(dateStr, pageWidth - margin - 5, yPos, { align: 'right' });

  yPos += 15;

  // Consultant Information Section
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Consultant Information', margin, yPos);
  yPos += 2;
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  const consultantName = paymentData.consultant_name || 'N/A';
  const consultantEmail = paymentData.consultant_email || 'N/A';

  doc.text(`Consultant Name:`, margin, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(consultantName, margin + 45, yPos);
  
  yPos += 7;
  doc.setFont(resolvedFont, 'normal');
  doc.text(`Email:`, margin, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(consultantEmail, margin + 45, yPos);

  yPos += 15;

  // Payment Details Section
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Payment Details', margin, yPos);
  yPos += 2;
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Table Headers
  doc.setFontSize(10);
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, yPos - 5, pageWidth - 2 * margin, 8, 'F');
  doc.setFont(resolvedFont, 'bold');
  doc.text('Description', margin + 2, yPos);
  doc.text('Amount', pageWidth - margin - 5, yPos, { align: 'right' });
  
  yPos += 10;

  // Payment Items
  doc.setFont(resolvedFont, 'normal');
  const amount = paymentData.amount || 0;
  const amountStr = `${CURRENCY} ${formatINR(amount, 2)}`;
  
  doc.text('Commission Payment', margin + 2, yPos);
  doc.text(amountStr, pageWidth - margin - 5, yPos, { align: 'right' });
  
  yPos += 10;

  // Total
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;
  
  doc.setFont(resolvedFont, 'bold');
  doc.setFontSize(12);
  doc.text('Total Amount Paid:', margin + 2, yPos);
  doc.text(amountStr, pageWidth - margin - 5, yPos, { align: 'right' });

  yPos += 15;

  // Payment Method Details
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  const paymentMode = paymentData.payment_mode || 'N/A';
  const referenceNo = paymentData.reference_number || 'N/A';
  
  doc.text(`Payment Mode:`, margin, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(paymentMode, margin + 40, yPos);
  
  if (referenceNo !== 'N/A' && paymentMode !== 'Cash') {
    yPos += 7;
    doc.setFont(resolvedFont, 'normal');
    doc.text(`Reference No:`, margin, yPos);
    doc.setFont(resolvedFont, 'bold');
    doc.text(referenceNo, margin + 40, yPos);
  }

  // Thank You Note
  yPos += 15;
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'italic');
  doc.text('Thank you for your valuable contribution to our institution.', pageWidth / 2, yPos, { align: 'center' });

  // Payment Made By Section
  yPos += 15;
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Payment Made By:', margin, yPos);
  yPos += 2;
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  const userName = paymentData.created_by_name || paymentData.created_by || 'Admin User';
  const userRole = paymentData.created_by_role || 'Admin';
  const userEmail = paymentData.created_by_email || '';
  
  doc.text('Name:', margin, yPos);
  doc.setFont(resolvedFont, 'bold');
  doc.text(`${userName} (${userRole})`, margin + 20, yPos);
  
  if (userEmail) {
    yPos += 7;
    doc.setFont(resolvedFont, 'normal');
    doc.text('Email:', margin, yPos);
    doc.setFont(resolvedFont, 'bold');
    doc.text(userEmail, margin + 20, yPos);
  }

  // Footer - Authorized Signature
  const footerY = pageHeight - 40;
  doc.setLineWidth(0.3);
  doc.line(pageWidth - margin - 60, footerY, pageWidth - margin, footerY);
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  doc.text('Authorized Signature', pageWidth - margin - 30, footerY + 7, { align: 'center' });

  // Disclaimer
  doc.setFontSize(8);
  doc.setFont(resolvedFont, 'italic');
  doc.setTextColor(100, 100, 100);
  doc.text('This is a computer-generated receipt and does not require a signature.', pageWidth / 2, pageHeight - 15, { align: 'center' });
  doc.text('For any queries, please contact the accounts department.', pageWidth / 2, pageHeight - 10, { align: 'center' });

  // Save the PDF
  const fileName = `Consultant_Receipt_${paymentData.receipt_no || 'Payment'}_${new Date().getTime()}.pdf`;
  doc.save(fileName);
  
  return doc;
};
